import {
  InternalPayItemConnectionClassType,
  InternalPayItemConnectionClassTypeItem,
} from '@cims-online/shared'
import {
  InternalPayItemConnectionsQuery,
  useInternalPayItemConnectionsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { useCallback, useMemo } from 'react'
import { useProjectContext } from 'context/ProjectContext'
import { usePayItemsModuleContext } from '../../../../PayItemsModuleContext'
import { WorkflowConnection } from '../../../../PayItemsModuleContext/types'

type P2I =
  InternalPayItemConnectionsQuery['project']['projectToInternalConnections'][0]
export type ProjectToInternal = Pick<P2I, 'group' | 'order'> & {
  internal: InternalPayItemConnectionClassTypeItem
  id: string
}
export type ProjectToWorkflow = Pick<
  WorkflowConnection,
  '__typename' | 'order' | 'workflow' | 'workflowId'
> & {
  id: string
  group:
    | InternalPayItemConnectionsQuery['project']['payItemConnectionGroups'][number]
    | null
}
export type PayItemsWorkflow = ProjectToInternal | ProjectToWorkflow
type InternalConnectionsContext = {
  refetch: () => Promise<void>
  fetching: boolean
  data: PayItemsWorkflow[]
  groups: DefaultOptionType[]
}

const { Provider, useContextFn } =
  createStrictContext<InternalConnectionsContext>('InternalConnectionsContext')

export const useInternalConnectionsContext = useContextFn

const InternalConnectionsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    fetching: fetchingPayItems,
    refetch: refetchPayItems,
    workflowConnections,
  } = usePayItemsModuleContext()
  const {
    data: response,
    loading: fetchingInternalConnections,
    refetch: refetchInternalConnections,
  } = useInternalPayItemConnectionsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const groups = useOptions({
    items: response?.project?.payItemConnectionGroups,
    type: 'default',
    value: 'id',
    label: 'name',
    tooltip: 'description',
    sort: 'name',
  })
  const data = useMemo(
    () => [
      ...InternalPayItemConnectionClassType.values.reduce((data, internal) => {
        const id = internal.id
        const p2i = response?.project?.projectToInternalConnections?.find(
          ({ internalConnectionId }) => internalConnectionId === id
        )
        if (p2i) data.push({ ...p2i, internal, id })
        else data.push({ order: 0, internal, id, group: null })
        return data
      }, Array<ProjectToInternal>()),
      ...workflowConnections.map<ProjectToWorkflow>((connection) => ({
        ...connection,
        group:
          response?.project?.payItemConnectionGroups.find(
            ({ id }) => id === connection.groupId
          ) ?? null,
        id: connection.workflowId,
      })),
    ],
    [
      response?.project?.payItemConnectionGroups,
      response?.project?.projectToInternalConnections,
      workflowConnections,
    ]
  )
  const refetch = useCallback(async () => {
    await Promise.all([refetchInternalConnections(), refetchPayItems()])
  }, [refetchInternalConnections, refetchPayItems])
  const fetching = fetchingInternalConnections || Boolean(fetchingPayItems)
  return (
    <Provider value={{ data, fetching, refetch, groups }}>{children}</Provider>
  )
}

export default InternalConnectionsProvider
